import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/ProtectedRoute";
import { useState, useEffect } from "react";
import axios from "axios";
import { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

/**
 * AdminRoute - Protects routes that should only be accessible to admin users.
 * For Master Database routes, also allows users with Master Database admin permissions.
 * Non-admin users are redirected to the home page.
 */
export default function AdminRoute({ children }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [masterDbAccess, setMasterDbAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  
  // Check if this is a Master Database route
  const isMasterDbRoute = location.pathname.startsWith('/master-database');
  
  // Check Master Database permissions for non-global-admins
  useEffect(() => {
    if (user && !user.isAdmin && isMasterDbRoute && masterDbAccess === null) {
      setCheckingAccess(true);
      axios.get(`${API}/master/permissions/me`)
        .then(response => {
          const level = response.data.level;
          // Allow access for admin, moderator, editor, or viewer levels
          setMasterDbAccess(level !== null);
        })
        .catch(() => {
          setMasterDbAccess(false);
        })
        .finally(() => {
          setCheckingAccess(false);
        });
    }
  }, [user, isMasterDbRoute, masterDbAccess]);
  
  // Debug logging
  console.log("AdminRoute check:", { user, isLoading, isAdmin: user?.isAdmin, isMasterDbRoute, masterDbAccess });
  
  // Show loading while checking auth status or Master DB access
  if (isLoading || checkingAccess) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  
  // Global admins always have access
  if (user?.isAdmin) {
    return children;
  }
  
  // For Master Database routes, check if user has permission
  if (isMasterDbRoute && masterDbAccess) {
    return children;
  }
  
  // Redirect unauthorized users to dashboard
  console.log("AdminRoute: Redirecting unauthorized user", user);
  return <Navigate to="/dashboard" replace />;
}
